import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, defineMessages, useIntl, FormattedNumber } from 'react-intl';

import {
    CalculatorStepPanel,
    Question,
    QuestionContainer,
    Colours,
    Sizes,
    useForm,
    getEmptyNumberPlaceholder,
    FormComputed,
} from '@ratehub/base-ui';
import {
    MORTGAGE_PLACEHOLDERS,
} from '@ratehub/mtg-common';
import {
    calculateNPER,
    calculatePMT,
} from '@ratehub/cc-common';


function CreditCardInterestInputStep({ stepNumber, ...otherProps }) {
    const form = useForm();
    const intl = useIntl();

    return (
        <CalculatorStepPanel
            stepNumber={stepNumber}
            isVisible
            {...otherProps}
        >
            <h3 className="heading rh-text-l weight-regular rh-pb-1_5 rh-mt-0">
                <FormattedMessage
                    id="calculators.CreditCardInterestInputStep.heading"
                    defaultMessage="Calculate your credit card interest"
                />
            </h3>

            <FormComputed
                name="nper"
                value={calculateNPER}
            />

            <FormComputed
                name="interestOwed"
                value={calculateInterestOwed}
            />

            <FormComputed
                name="pmt"
                value={calculatePMT}
            />

            <Question
                className="condensed"
                field={{
                    name: 'currentBalance',
                    type: 'integer',
                    validators: [
                        {
                            fn: value => value > 0,
                            message: (
                                <FormattedMessage
                                    id="calculators.CreditCardInterestInputStep.validatorAtLeastOneDollar"
                                    defaultMessage="Must be at least $1 or a whole number"
                                />
                            ),
                        },
                    ],
                }}
                type="currency"
                placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.currentBalanceLabel"
                        defaultMessage="Current credit card balance"
                    />
                )}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.currentBalanceQuicktip"
                        defaultMessage="The amount of money owed on your credit card."
                    />
                )}
                isSideBySide
            />

            <Question
                className="condensed"
                field={{
                    name: 'interestRate',
                    type: 'float',
                    validators: [
                        {
                            fn: value => value > 0,
                            message: (
                                <FormattedMessage
                                    id="calculators.CreditCardInterestInputStep.validatorCannotBeZero"
                                    defaultMessage="Cannot be 0%"
                                />
                            ),
                        },
                    ],
                }}
                decimalScale={2}
                type="percent"
                placeholder={MORTGAGE_PLACEHOLDERS.PERCENT_PLACEHOLDER}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.interestRateLabel"
                        defaultMessage="Interest rate"
                    />
                )}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.interestRateQuicktip"
                        defaultMessage="Credit cards typically charge an annual rate of 12% to 20% on the balance owed."
                    />
                )}
                tooltipMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.interestRateTooltip"
                        defaultMessage="As standard, interest is expressed as annual percentage rate (APR).
                            In real terms, APR is divided by 365 and charged every day you owe a balance until you pay back what is owed in full."
                    />
                )}
                isSideBySide
            />

            <Question
                className="condensed"
                field={{
                    name: 'calculationOption',
                    type: 'choice',
                    options: CALCULATION_OPTIONS,
                    value: INTEREST_CALCULATION_VALUES.monthlyPayment,
                }}
                type="radio"
                labelMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.calculationOptionLabel"
                        defaultMessage="Calculate your interest"
                    />
                )}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.calculationOptionQuicktip"
                        defaultMessage="Choose how you would like to calculate the amount of interest owed."
                    />
                )}
                isSideBySide
            />

            <Question
                className="condensed"
                field={{
                    name: 'monthlyPaymentAmount',
                    type: 'integer',
                    validators: [
                        {
                            fn: value => value <= form.values.currentBalance,
                            message: {
                                id: 'calculators.CreditCardInterestInputStep.paymentGreaterThanBalanceValidator',
                                defaultMessage: 'Cannot be greater than current credit card balance.',
                            },
                        },
                        {
                            fn: value => value >= getMinMonthlyPayment(form.values.currentBalance),
                            message: {
                                id: 'calculators.CreditCardInterestInputStep.invalidPaymentAmountValidator',
                                defaultMessage: 'Must be at least 3% of current credit card balance or $10 - whichever is greater.',
                            },
                        },
                    ],
                }}
                type="currency"
                placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.monthlyPaymentLabel"
                        defaultMessage="Monthly payment"
                    />
                )}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.monthlyPaymentQuicktip"
                        defaultMessage="How much money you will put towards paying back your balance every month."
                    />
                )}
                isVisible={form.values.calculationOption === INTEREST_CALCULATION_VALUES.monthlyPayment}
                isSideBySide
            />

            <Question
                className="condensed"
                field={{
                    name: 'totalPaymentMonths',
                    type: 'integer',
                    validators: [
                        {
                            fn: value => {
                                // Must enter at least one month AND no more months than would result in
                                // a monthly payment that is less than the minimum monthly payment.
                                const minMonthlyPayment = getMinMonthlyPayment(form.values.currentBalance);
                                return value > 0 && form.values.pmt >= minMonthlyPayment;
                            },
                            message: (
                                <FormattedMessage
                                    id="calculators.CreditCardInterestInputStep.validatorInvalidNumberOfMonths"
                                    defaultMessage="Invalid. Try entering fewer months, as the “Monthly payment” is too small."
                                />
                            ),
                        },
                    ],
                }}
                placeholder={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.totalPaymentMonthsPlaceholder"
                        defaultMessage="Enter # of months"
                    />
                )}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.totalPaymentMonthsLabel"
                        defaultMessage="Desired time to pay off balance"
                    />
                )}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.totalPaymentMonthsQuicktip"
                        defaultMessage="Indicate how many months you’d like to be debt free."
                    />
                )}
                isVisible={form.values.calculationOption === INTEREST_CALCULATION_VALUES.monthsRemaining}
                isSideBySide
            />

            <QuestionContainer
                className="monthsRemaining condensed"
                labelMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.monthsRemainingResultLabel"
                        defaultMessage="Time to pay off balance"
                    />
                )}
                labelFor="monthsRemainingResult"
                isVisible={form.values.calculationOption === INTEREST_CALCULATION_VALUES.monthlyPayment}
                isSideBySide
            >
                <span
                    id="monthsRemainingResult"
                    className="rh-title-xs weight-medium rh-fg-blueberry-dark"
                >
                    <If condition={!form.values.nper}>
                        <FormattedMessage
                            id="calculators.CreditCardInterestInputStep.monthsRemainingDefaultMessage"
                            defaultMessage="- months"
                        />
                    </If>
                    <If condition={form.values.nper > 12}>
                        <FormattedMessage
                            id="calculators.CreditCardInterestInputStep.yearsRemainingMessage"
                            className="rh-title-xs weight-medium rh-fg-blueberry-dark"
                            defaultMessage="{years, plural,
                                one {{years} year, }
                                other {{years} years, }
                            }"
                            values={{
                                years: Math.floor(form.values.nper / 12),
                            }}
                        />
                    </If>
                    <If condition={form.values.nper}>
                        <FormattedMessage
                            id="calculators.CreditCardInterestInputStep.monthsRemainingMessage"
                            className="rh-title-xs weight-medium rh-fg-blueberry-dark"
                            defaultMessage="{months, plural,
                                one {{months} month}
                                other {{months} months}
                            }"
                            values={{
                                months: Math.ceil(form.values.nper % 12),
                            }}
                        />
                    </If>
                </span>
            </QuestionContainer>

            <QuestionContainer
                className="monthlyPayment condensed"
                labelMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.monthlyPaymentResultsLabel"
                        defaultMessage="Monthly payment"
                    />
                )}
                labelFor="monthlyPaymentResults"
                isVisible={form.values.calculationOption === INTEREST_CALCULATION_VALUES.monthsRemaining}
                isSideBySide
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.monthlyPaymentResultsQuicktip"
                        defaultMessage="Must be at least 3% of current credit card balance or $10 - whichever is greater."
                    />
                )}
            >
                <span
                    id="monthlyPaymentResults"
                    className="rh-title-xs weight-medium rh-fg-blueberry-dark"
                >
                    <If condition={!form.values.pmt}>
                        {getEmptyNumberPlaceholder('CAD0', intl)}
                    </If>

                    <If condition={form.values.pmt}>
                        <FormattedNumber
                            value={form.values.pmt}
                            format="CAD0"
                        />
                    </If>
                </span>
            </QuestionContainer>

            <QuestionContainer
                className="totalInterestOwed condensed"
                labelMessage={(
                    <FormattedMessage
                        id="calculators.CreditCardInterestInputStep.interestOwedResultLabel"
                        defaultMessage="Total interest owed"
                    />
                )}
                labelFor="interestOwnedResult"
                isVisible={form.values.calculationOption !== null}
                isSideBySide
            >
                <span
                    id="interestOwnedResult"
                    className="rh-title-xs weight-medium rh-fg-blueberry-dark"
                >
                    <If condition={!form.values.interestOwed}>
                        {getEmptyNumberPlaceholder('CAD2', intl)}
                    </If>

                    <If condition={form.values.interestOwed}>
                        <FormattedNumber
                            value={form.values.interestOwed}
                            format="CAD2"
                        />
                    </If>
                </span>
            </QuestionContainer>
        </CalculatorStepPanel>
    );
}

CreditCardInterestInputStep.propTypes = {
    stepNumber: PropTypes.number.isRequired,
};

const MESSAGES = defineMessages({
    monthlyPayment: {
        id: 'calculators.CreditCardInterestInputStep.monthlyPaymentOptionLabel',
        defaultMessage: 'By your monthly payment',
    },
    monthsRemaining: {
        id: 'calculators.CreditCardInterestInputStep.monthsRemainingOptionLabel',
        defaultMessage: 'By number of months to pay off balance',
    },
});

const INTEREST_CALCULATION_VALUES = {
    monthlyPayment: 'monthlyPayment',
    monthsRemaining: 'monthsRemaining',
};

const CALCULATION_OPTIONS = [
    {
        value: INTEREST_CALCULATION_VALUES.monthlyPayment,
        label: MESSAGES.monthlyPayment,
    },
    {
        value: INTEREST_CALCULATION_VALUES.monthsRemaining,
        label: MESSAGES.monthsRemaining,
    },
];

function calculateInterestOwed({ calculationOption, monthlyPaymentAmount, nper, pmt, totalPaymentMonths, currentBalance }) {
    if (calculationOption === INTEREST_CALCULATION_VALUES.monthlyPayment) {
        if (!monthlyPaymentAmount || !currentBalance) {
            return;
        }

        return (monthlyPaymentAmount * nper) - currentBalance;
    }

    if (!totalPaymentMonths || !currentBalance) {
        return;
    }

    return (pmt * totalPaymentMonths) - currentBalance;
}

// Min monthly payment should be 3% of current balance or $10, whichever is higher
function getMinMonthlyPayment(currentBalance) {
    return currentBalance * 0.03 > 10
        ? currentBalance * 0.03
        : 10;
}

const StyledInputStep = styled(observer(CreditCardInterestInputStep))`
    .heading {
        border-bottom: 1px solid ${Colours.STONE};
    }

    .output {
        height: ${Sizes.SPACING.TWO};
    }

    .condensed {
        margin-bottom: ${Sizes.SPACING.ONE};
    }
`;

export default StyledInputStep;
